import { clsx } from 'clsx';

/**
 * Renders one of the 409 human-approved PB toolbar icons (handoff 2026-09-04 — see
 * {@code docs/design/pb-icons/HANDOFF.md}).
 *
 * <p>The artwork is served as a static file from {@code /pb-icons/<id>.svg}, not bundled:
 * nginx serves it straight from the web root with normal HTTP caching, so 409 icons cost
 * the JS bundle nothing and a screen downloads only the handful it actually shows.
 *
 * <p>The SVGs carry hard-coded palette fills — they deliberately do NOT use
 * {@code currentColor} — so an {@code <img>} is the right element: there is nothing to
 * recolour, and an img cannot leak the file's styles into the page. It also means the icon
 * must never be given a text colour or a fill; sizing is the only knob.
 *
 * <p>The files carry no width/height (viewBox 0 0 24 24 only), so both attributes are set
 * here. Default 20px is the lower end of the handoff's recommended 20–24 toolbar size and
 * matches the 20×20 hand-drawn icons it sits beside during the transition.
 *
 * <p>Always {@code alt=""} + {@code aria-hidden}: {@code IconButton} already puts PB's
 * caption on the button as {@code aria-label} + {@code title}, and a second accessible
 * name here would double-announce it.
 *
 * <p>Disabled state needs nothing extra — IconButton's {@code disabled:opacity-40} sits on
 * the &lt;button&gt;, and CSS opacity applies to the whole subtree, so the image dims with
 * the button exactly like the inline SVGs do.
 */
export function PbIcon({
  id,
  size = 20,
  className,
}: {
  /** Approved icon id, e.g. {@code "icon_print_label"}. */
  id: string;
  /** Rendered square size in px (handoff recommends 20–24). */
  size?: number;
  className?: string;
}) {
  return (
    <img
      src={`/pb-icons/${id}.svg`}
      width={size}
      height={size}
      alt=""
      aria-hidden
      draggable={false}
      className={clsx('pb-icon shrink-0', className)}
    />
  );
}
